/**
 * 디바이스 전원 상태 표시 (우상단 오버레이)
 * - 1차: Android 키오스크 앱이 주입하는 'nativeBattery' CustomEvent
 *   (Android System WebView에서 navigator.getBattery 가 제거되어 있음)
 * - 2차: Battery Status API (navigator.getBattery) — 일반 브라우저용 폴백
 * - 충전 중: AC 플러그 아이콘
 * - 배터리: 배터리 아이콘 + %
 * - 미지원/오류 시 "?" 로 표시 (오버레이 자체는 항상 노출)
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './BatteryIndicator.css';

const QUEST_SPOT_PATH = '/quest-tag';

/** 퀘스트 스팟과 그 하위 경로에서는 고객 화면을 가리지 않도록 전원 상태를 숨긴다. */
export function shouldHideBatteryIndicator(pathname) {
  const path = typeof pathname === 'string' ? pathname : '';
  return path === QUEST_SPOT_PATH || path.startsWith(`${QUEST_SPOT_PATH}/`);
}

function BatteryIndicatorStatus() {
  // status: 'loading' | 'unsupported' | 'error' | 'ok'
  const [status, setStatus] = useState('loading');
  const [state, setState] = useState({ charging: null, level: null });

  // 네이티브 브리지(Android 키오스크 앱)에서 주입하는 배터리 이벤트 — 항상 우선
  useEffect(() => {
    const apply = (detail) => {
      if (!detail) return;
      const charging = detail.charging === true;
      const levelPct = typeof detail.levelPct === 'number' ? detail.levelPct : -1;
      const level = levelPct >= 0 ? levelPct / 100 : null;
      setState({ charging, level });
      setStatus('ok');
    };
    // 마운트 시점에 이미 주입돼 있던 값 즉시 반영 (페이지 로드 시 evaluateJavascript 가
    // React 마운트보다 먼저 실행되어 이벤트가 유실되는 race 방지)
    if (typeof window !== 'undefined' && window.__nativeBattery) {
      apply(window.__nativeBattery);
    }
    const onNative = (e) => apply(e && e.detail);
    window.addEventListener('nativeBattery', onNative);
    return () => window.removeEventListener('nativeBattery', onNative);
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || typeof navigator.getBattery !== 'function') {
      // eslint-disable-next-line no-console
      console.warn('[BatteryIndicator] navigator.getBattery is not available — nativeBattery 이벤트 대기');
      setStatus('unsupported');
      return;
    }
    let battery = null;
    let mounted = true;

    const update = () => {
      if (!battery || !mounted) return;
      setState({ charging: battery.charging, level: battery.level });
      setStatus('ok');
    };

    navigator.getBattery().then((b) => {
      if (!mounted) return;
      battery = b;
      // eslint-disable-next-line no-console
      console.info('[BatteryIndicator] charging=%s level=%s', b.charging, b.level);
      update();
      battery.addEventListener('chargingchange', update);
      battery.addEventListener('levelchange', update);
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[BatteryIndicator] getBattery failed:', err);
      if (mounted) setStatus('error');
    });

    return () => {
      mounted = false;
      if (battery) {
        battery.removeEventListener('chargingchange', update);
        battery.removeEventListener('levelchange', update);
      }
    };
  }, []);

  const pct = Math.round((state.level || 0) * 100);
  const charging = state.charging === true;
  const isLow = status === 'ok' && !charging && pct <= 20;
  const fillWidth = Math.max(0, Math.min(20, 20 * (pct / 100)));
  const knownState = status === 'ok';

  let label;
  if (status === 'loading') label = '…';
  else if (status === 'unsupported') label = 'N/A';
  else if (status === 'error') label = 'ERR';
  else label = charging ? 'AC' : `${pct}%`;

  return (
    <div className={`battery-indicator${charging && knownState ? ' is-charging' : ''}${isLow ? ' is-low' : ''}${!knownState ? ' is-unknown' : ''}`}
         title={status === 'ok'
           ? (charging ? `AC 연결 (${pct}%)` : `배터리 ${pct}%`)
           : `Battery API ${status}`}>
      {knownState && charging && (
        <svg className="bi-icon" width="16" height="20" viewBox="0 0 16 20" aria-hidden="true">
          <path d="M5 0v3M11 0v3M3 3h10v6a5 5 0 0 1-10 0V3zM8 14v6"
                stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round"/>
        </svg>
      )}
      {knownState && !charging && (
        <svg className="bi-icon" width="28" height="14" viewBox="0 0 28 14" aria-hidden="true">
          <rect x="0.6" y="0.6" width="23.8" height="12.8" rx="2" ry="2"
                stroke="currentColor" strokeWidth="1.2" fill="none"/>
          <rect x="2" y="2" width={fillWidth} height="10" rx="1" ry="1" fill="currentColor"/>
          <rect x="25.2" y="4" width="2.4" height="6" rx="0.6" ry="0.6" fill="currentColor"/>
        </svg>
      )}
      {!knownState && (
        <svg className="bi-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" fill="none"/>
          <text x="8" y="11" textAnchor="middle" fontSize="9" fill="currentColor"
                fontFamily="monospace" fontWeight="700">?</text>
        </svg>
      )}
      <span className="bi-pct">{label}</span>
    </div>
  );
}

export function BatteryIndicatorForPath({ pathname }) {
  // 상태 본체를 렌더하지 않아 퀘스트 스팟에서는 네이티브 이벤트·Battery API 구독도 만들지 않는다.
  if (shouldHideBatteryIndicator(pathname)) return null;
  return <BatteryIndicatorStatus />;
}

export default function BatteryIndicator() {
  const { pathname } = useLocation();
  return <BatteryIndicatorForPath pathname={pathname} />;
}
